import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserCircle, Car, Calendar, DollarSign, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export const GestionPage = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('conductores');
  const [conductoresData, setConductoresData] = useState([]);
  const [vehiculosData, setVehiculosData] = useState([]);
  const [filters, setFilters] = useState({
    fecha_desde: '',
    fecha_hasta: ''
  });

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters({
      fecha_desde: firstDay.toISOString().split('T')[0],
      fecha_hasta: lastDay.toISOString().split('T')[0]
    });
  }, []);

  const handleBuscar = async () => {
    if (!filters.fecha_desde || !filters.fecha_hasta) {
      toast.error('Debe seleccionar rango de fechas');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/reportes/gestion', {
        params: {
          fecha_desde: filters.fecha_desde,
          fecha_hasta: filters.fecha_hasta
        }
      });

      // El backend devuelve { conductores: [...], vehiculos: [...] }
      const data = response.data;
      setConductoresData(data.conductores || []);
      setVehiculosData(data.vehiculos || []);
      
      const total = (data.conductores?.length || 0) + (data.vehiculos?.length || 0);
      toast.success(`${total} registros cargados`);
    } catch (error) {
      toast.error('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters.fecha_desde && filters.fecha_hasta) {
      handleBuscar();
    }
  }, [filters.fecha_desde, filters.fecha_hasta]);

  const totalViajesConductores = Array.isArray(conductoresData) 
    ? conductoresData.reduce((sum, c) => sum + (c.total_servicios || 0), 0) 
    : 0;
  const totalFacturadoConductores = Array.isArray(conductoresData) 
    ? conductoresData.reduce((sum, c) => sum + (c.total_facturado || 0), 0) 
    : 0;
  
  const totalViajesVehiculos = Array.isArray(vehiculosData) 
    ? vehiculosData.reduce((sum, v) => sum + (v.total_servicios || 0), 0) 
    : 0;
  const totalFacturadoVehiculos = Array.isArray(vehiculosData) 
    ? vehiculosData.reduce((sum, v) => sum + (v.total_facturado || 0), 0) 
    : 0;

  return (
    <div className="space-y-6" data-testid="gestion-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Reporte de Gestión</h1>
        <p className="text-gray-600 mt-1">Análisis de desempeño de conductores y vehículos</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Período de Análisis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="fecha_desde">Fecha Desde</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={18} />
                <Input
                  id="fecha_desde"
                  type="date"
                  value={filters.fecha_desde}
                  onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
                  className="pl-10"
                  data-testid="fecha-desde-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="fecha_hasta">Fecha Hasta</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={18} />
                <Input
                  id="fecha_hasta"
                  type="date"
                  value={filters.fecha_hasta}
                  onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
                  className="pl-10"
                  data-testid="fecha-hasta-input"
                />
              </div>
            </div>

            <div className="flex items-end">
              <Button
                onClick={() => handleBuscar()}
                disabled={loading}
                className="w-full"
                data-testid="buscar-button"
              >
                <TrendingUp size={18} className="mr-2" />
                {loading ? 'Analizando...' : 'Analizar'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="conductores" data-testid="tab-conductores">
            <UserCircle size={18} className="mr-2" />
            Conductores
          </TabsTrigger>
          <TabsTrigger value="vehiculos" data-testid="tab-vehiculos">
            <Car size={18} className="mr-2" />
            Vehículos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conductores" className="space-y-4">
          {/* Estadísticas Conductores */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total de Viajes</p>
                    <p className="text-3xl font-bold text-primary mt-2">{totalViajesConductores}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="text-primary" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Facturado</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">
                      ${totalFacturadoConductores.toLocaleString('es-CL')}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <DollarSign className="text-green-600" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-6">
              {conductoresData.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Conductor</TableHead>
                      <TableHead>RUT</TableHead>
                      <TableHead className="text-center">Total Viajes</TableHead>
                      <TableHead className="text-right">Total Facturado</TableHead>
                      <TableHead className="text-right">Promedio por Viaje</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conductoresData.map((conductor, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{index + 1}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <UserCircle size={16} className="text-primary" />
                            {conductor.nombre}
                          </div>
                        </TableCell>
                        <TableCell>{conductor.rut}</TableCell>
                        <TableCell className="text-center">
                          <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                            {conductor.total_servicios || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-bold text-green-600">
                          ${(conductor.total_facturado || 0).toLocaleString('es-CL')}
                        </TableCell>
                        <TableCell className="text-right text-gray-600">
                          ${conductor.total_servicios > 0 ? Math.round(conductor.total_facturado / conductor.total_servicios).toLocaleString('es-CL') : 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <TrendingUp size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No hay datos para mostrar</p>
                  <p className="text-sm mt-1">Seleccione un período y haga clic en Analizar</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vehiculos" className="space-y-4">
          {/* Estadísticas Vehículos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total de Viajes</p>
                    <p className="text-3xl font-bold text-primary mt-2">{totalViajesVehiculos}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="text-primary" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Facturado</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">
                      ${totalFacturadoVehiculos.toLocaleString('es-CL')}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <DollarSign className="text-green-600" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-6">
              {vehiculosData.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Vehículo</TableHead>
                      <TableHead>Patente</TableHead>
                      <TableHead className="text-center">Total Viajes</TableHead>
                      <TableHead className="text-right">Total Facturado</TableHead>
                      <TableHead className="text-right">Promedio por Viaje</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehiculosData.map((vehiculo, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{index + 1}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Car size={16} className="text-primary" />
                            {vehiculo.marca} {vehiculo.modelo}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono font-semibold">{vehiculo.patente}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                            {vehiculo.total_servicios || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-bold text-green-600">
                          ${(vehiculo.total_facturado || 0).toLocaleString('es-CL')}
                        </TableCell>
                        <TableCell className="text-right text-gray-600">
                          ${vehiculo.total_servicios > 0 ? Math.round(vehiculo.total_facturado / vehiculo.total_servicios).toLocaleString('es-CL') : 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <TrendingUp size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No hay datos para mostrar</p>
                  <p className="text-sm mt-1">Seleccione un período y haga clic en Analizar</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
